import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search as SearchIcon, ArrowRight, PenLine, Users, Send, CheckSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import LamnsoKeyboard from '@/components/LamnsoKeyboard';
import { SourceBadge } from '@/components/SourceBadge';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

export default function SearchPage() {
  const { isAuthenticated, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [translations, setTranslations] = useState([]);
  const [detectedLang, setDetectedLang] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [showSuggest, setShowSuggest] = useState(false);
  const [suggestForm, setSuggestForm] = useState({ translation: '', note: '', example: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [parsedIntent, setParsedIntent] = useState(null);
  const [dictSenses, setDictSenses] = useState([]);
  const [dictExamples, setDictExamples] = useState([]);
  const [litExamples, setLitExamples] = useState([]);
  const [pronunciations, setPronunciations] = useState([]);
  const [sciNames, setSciNames] = useState([]);
  const [locations, setLocations] = useState([]);
  const [conversation, setConversation] = useState(null);
  const [sentence, setSentence] = useState(null);

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) { setQuery(q); doSearch(q); }
  }, []); // eslint-disable-line

  const doSearch = async (q) => {
    if (!q?.trim()) return;
    setLoading(true);
    setSubmitted(false);
    setShowSuggest(false);
    try {
      const res = await api.get(`/search/smart?q=${encodeURIComponent(q)}&limit=20`);
      const data = res.data;
      setTranslations(data.translations || []);
      const dir = data.parsed?.direction;
      setDetectedLang(dir === 'lamnso_to_english' ? 'lamnso' : dir === 'english_to_lamnso' ? 'english' : null);
      setParsedIntent(data.parsed);
      setDictSenses(data.dict_senses || []);
      setDictExamples(data.dict_examples || []);
      setLitExamples(data.literature_examples || []);
      setPronunciations(data.pronunciations || []);
      setSciNames(data.scientific_names || []);
      setLocations(data.locations || []);
      setConversation(data.conversation || null);
      setSentence(data.sentence || null);
      setTotal(data.total || 0);
    } catch { setTranslations([]); }
    setLoading(false);
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    setSearchParams({ q: query });
    doSearch(query);
  };

  const handleKeyboardInsert = (char) => setQuery(prev => prev + char);
  const handleKeyboardDelete = () => setQuery(prev => prev.slice(0, -1));

  const handleSuggest = async () => {
    setSubmitting(true);
    try {
      await api.post('/suggestions', {
        query_text: query,
        direction: detectedLang === 'lamnso' ? 'lamnso_to_english' : 'english_to_lamnso',
        suggested_translation: suggestForm.translation,
        note: suggestForm.note,
        example_context: suggestForm.example
      });
      setSubmitted(true);
      setShowSuggest(false);
      setSuggestForm({ translation: '', note: '', example: '' });
    } catch (err) { console.error(err) }
    setSubmitting(false);
  };

  const statusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-[#EAF1EA] text-[#588157]';
      case 'candidate': return 'bg-[#F5F5F4] text-[#57534E]';
      case 'rejected': return 'bg-[#E8F5E9] text-[#4CAF50]';
      default: return 'bg-[#F5F5F4] text-[#57534E]';
    }
  };

  return (
    <div className="max-w-lg mx-auto px-4 pt-4 pb-20 sm:max-w-2xl">
      <h1 className="text-xl font-bold text-[#1C1917] mb-3" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="search-title">
        Search
      </h1>

      {/* Search bar */}
      <form onSubmit={handleSearch} className="mb-2">
        <div className="relative">
          <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A8A29E]" />
          {showKeyboard ? (
            <div className="block w-full rounded-2xl border-0 py-3.5 pl-12 pr-28 text-[#1C1917] shadow-sm ring-2 ring-inset ring-[#4CAF50] bg-white min-h-[52px] flex items-center"
              style={{ fontSize: '16px' }} onClick={() => {}} data-testid="search-input-display">
              <span>{query}</span><span className="animate-pulse text-[#4CAF50] font-light">|</span>
            </div>
          ) : (
            <input
              type="text" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Lamnso' or English..."
              className="block w-full rounded-2xl border-0 py-3.5 pl-12 pr-28 text-[#1C1917] shadow-sm ring-1 ring-inset ring-[#E7E5E4] focus:ring-2 focus:ring-inset focus:ring-[#4CAF50] bg-white"
              style={{ fontSize: '16px' }}
              data-testid="search-input"
            />
          )}
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
            <button type="button" onClick={() => setShowKeyboard(!showKeyboard)}
              className={`px-2.5 py-1.5 text-[10px] font-bold rounded-lg transition-colors ${showKeyboard ? 'bg-[#4CAF50] text-white' : 'bg-[#E8F5E9] text-[#4CAF50]'}`}
              data-testid="toggle-keyboard-button">
              {showKeyboard ? 'ABC' : 'əŋ'}
            </button>
            <button type="submit"
              className="bg-[#4CAF50] hover:bg-[#3D8B40] text-white px-4 py-1.5 rounded-xl text-sm font-semibold"
              data-testid="search-button">
              Go
            </button>
          </div>
        </div>
      </form>

      {showKeyboard && (
        <div className="mb-20">
          <LamnsoKeyboard onInsert={handleKeyboardInsert} onDelete={handleKeyboardDelete} onReturn={handleSearch} />
        </div>
      )}

      {/* Language indicator */}
      {detectedLang && translations.length > 0 && (
        <div className="flex items-center gap-2 mb-3" data-testid="detected-language">
          <span className="text-[10px] text-[#A8A29E]">
            Detected: <strong className="text-[#57534E]">{detectedLang === 'both' ? "Lamnso' & English" : detectedLang === 'lamnso' ? "Lamnso'" : 'English'}</strong>
          </span>
        </div>
      )}

      {/* Results */}
      {loading ? (
        <div className="text-center py-16">
          <div className="w-8 h-8 border-4 border-[#E7E5E4] border-t-[#4CAF50] rounded-full animate-spin mx-auto" />
        </div>
      ) : (translations.length > 0 || dictSenses.length > 0 || sciNames.length > 0 || locations.length > 0 || sentence) ? (
        <div className="space-y-2.5" data-testid="search-results">
          {/* Conversational header */}
          {conversation && conversation.type !== 'no_result' && (
            <div className="bg-white rounded-2xl border border-[#E7E5E4] p-4 mb-1" data-testid="conversational-header">
              <p className="text-sm text-[#1C1917] leading-relaxed">
                <ConversationalText text={conversation.header} />
              </p>
              {conversation.subtext && (
                <p className="text-[10px] text-[#A8A29E] mt-1">{conversation.subtext}</p>
              )}
            </div>
          )}

          {/* Sentence response */}
          {sentence && (
            <div className="bg-white rounded-2xl border border-[#E7E5E4] p-4 mb-1" data-testid="sentence-response">
              {sentence.ai_note && (
                <p className="text-sm text-[#1C1917] leading-relaxed mb-3">{sentence.ai_note}</p>
              )}
              {sentence.word_breakdown?.length > 0 && (
                <div className="mb-3">
                  <p className="text-[10px] font-bold text-[#4CAF50] uppercase tracking-wider mb-1.5">Word-by-word</p>
                  <div className="flex flex-wrap gap-1.5">
                    {sentence.word_breakdown.map((w, i) => (
                      <span key={`wb-${i}`} className="inline-flex items-center gap-1 px-2 py-1 bg-[#E8F5E9] rounded-lg text-xs">
                        <span className="text-[#57534E]">{w.english}</span>
                        <span className="text-[#A8A29E]">=</span>
                        <span className="font-semibold text-[#4CAF50]">{w.lamnso}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {sentence.literature_matches?.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold text-[#78716C] uppercase tracking-wider mb-1.5">Similar in literature</p>
                  {sentence.literature_matches.map((m, i) => (
                    <div key={`lit-${i}`} className="border-l-2 border-[#78716C]/30 pl-3 py-1 mb-1">
                      <p className="text-xs text-[#1C1917]">{m.lamnso?.slice(0, 120)}</p>
                      <p className="text-[10px] text-[#57534E] italic">{m.english?.slice(0, 100)}</p>
                      <span className="text-[9px] text-[#A8A29E]">{m.book_name_en} {m.chapter}:{m.verse}</span>
                    </div>
                  ))}
                </div>
              )}
              {!sentence.ai_note && !sentence.word_breakdown?.length && !sentence.literature_matches?.length && (
                <p className="text-sm text-[#57534E]">We can't translate full sentences yet, but try searching individual words.</p>
              )}
            </div>
          )}

          <p className="text-[10px] text-[#A8A29E] font-medium">{total} result{total !== 1 ? 's' : ''}</p>
          {translations.map((t) => (
            <div key={t.translation_id} className="relative" data-testid={`result-${t.translation_id}`}>
              <Link to={`/dictionary/${t.lamnso_word?.word_id || t.english_word?.word_id}`}
                className="block">
                <div className="bg-white rounded-2xl border border-[#E7E5E4] p-4 active:scale-[0.99] transition-transform">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                      <span className="text-base font-semibold text-[#1C1917] truncate" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        {t.lamnso_word?.text || '?'}
                      </span>
                      <ArrowRight className="w-3.5 h-3.5 text-[#A8A29E] flex-shrink-0" />
                      <span className="text-base font-semibold text-[#4CAF50] truncate" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        {t.english_word?.text || '?'}
                      </span>
                    </div>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ml-2 ${statusColor(t.status)}`}>
                      {t.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1 bg-[#F5F5F4] rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${t.confidence_score >= 0.9 ? 'bg-[#588157]' : t.confidence_score >= 0.7 ? 'bg-[#D4A373]' : 'bg-[#4CAF50]'}`}
                        style={{ width: `${Math.round(t.confidence_score * 100)}%` }} />
                    </div>
                    <span className="text-[10px] font-medium text-[#57534E]">{Math.round(t.confidence_score * 100)}%</span>
                    <SourceBadge source={t.source} variant="tag" />
                  </div>
                </div>
              </Link>
              {t.status !== 'confirmed' && user?.level >= 4 && (
                <Link to={`/validate?focus=${t.translation_id}`}
                  className="absolute top-3 right-3 flex items-center gap-1 text-[9px] font-bold px-2 py-1 rounded-lg bg-[#FDF6F0] text-[#D4A373] hover:bg-[#F5EDE4] transition-colors z-10"
                  data-testid={`review-link-${t.translation_id}`}
                  onClick={(e) => e.stopPropagation()}>
                  <CheckSquare className="w-3 h-3" /> Review
                </Link>
              )}
            </div>
          ))}

          {/* Dictionary senses */}
          {dictSenses.length > 0 && (
            <div className="mt-3">
              <p className="text-[10px] font-bold text-[#D4A373] uppercase tracking-wider mb-2">Dictionary Meanings</p>
              {dictSenses.map((s, i) => (
                <div key={s.sense_id} className="bg-white rounded-xl border border-[#E7E5E4] p-3 mb-1.5">
                  <div className="flex gap-2">
                    <span className="text-xs font-bold text-[#D4A373]">{s.sense_order || i + 1}.</span>
                    <div>
                      <p className="text-xs text-[#1C1917]">{s.sense}</p>
                      {s.pos && <span className="text-[9px] bg-[#F5F5F4] text-[#57534E] px-1.5 py-0.5 rounded mt-1 inline-block">{s.pos}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Dictionary examples */}
          {dictExamples.length > 0 && (
            <div className="mt-3">
              <p className="text-[10px] font-bold text-[#588157] uppercase tracking-wider mb-2">Example Sentences</p>
              {dictExamples.map((ex) => (
                <div key={ex.example_id} className="border-l-2 border-[#588157]/30 pl-3 py-1 mb-1.5">
                  <p className="text-xs text-[#1C1917]">{ex.lamnso}</p>
                  {ex.english && <p className="text-[10px] text-[#57534E] italic">{ex.english}</p>}
                </div>
              ))}
            </div>
          )}

          {/* Literature usage */}
          {litExamples.length > 0 && (
            <div className="mt-3">
              <p className="text-[10px] font-bold text-[#78716C] uppercase tracking-wider mb-2">Used in Literature</p>
              {litExamples.map((v) => (
                <div key={v.verse_id} className="border-l-2 border-[#78716C]/30 pl-3 py-1 mb-1.5">
                  <p className="text-xs text-[#1C1917]">{v.lamnso?.slice(0, 150)}{v.lamnso?.length > 150 ? '...' : ''}</p>
                  {v.english && <p className="text-[10px] text-[#57534E] italic">{v.english?.slice(0, 120)}</p>}
                  <span className="text-[9px] text-[#A8A29E]">{v.book_name_en} {v.chapter}:{v.verse}</span>
                </div>
              ))}
            </div>
          )}

          {/* Scientific names */}
          {sciNames.length > 0 && (
            <div className="mt-3">
              <p className="text-[10px] font-bold text-[#6D28D9] uppercase tracking-wider mb-2">Scientific Names</p>
              {sciNames.map((s) => (
                <div key={s.entry_id} className="bg-white rounded-xl border border-[#E7E5E4] p-3 mb-1.5">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xs font-semibold text-[#1C1917]">{s.lamnso}</span>
                      <span className="text-[10px] text-[#A8A29E] ml-2 italic">{s.scientific_name}</span>
                    </div>
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#F3F0FF] text-[#6D28D9]">Scientific</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Locations */}
          {locations.length > 0 && (
            <div className="mt-3">
              <p className="text-[10px] font-bold text-[#B45309] uppercase tracking-wider mb-2">Places</p>
              {locations.map((l) => (
                <div key={l.entry_id} className="bg-white rounded-xl border border-[#E7E5E4] p-3 mb-1.5">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xs font-semibold text-[#1C1917]">{l.name}</span>
                      {l.subdivision && <span className="text-[10px] text-[#A8A29E] ml-2">{l.subdivision}</span>}
                    </div>
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#FFFBEB] text-[#B45309] capitalize">
                      {l.location_type.replace(/_/g, ' ')}
                    </span>
                  </div>
                  {l.description && <p className="text-[10px] text-[#57534E] mt-0.5">{l.description}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : query && !loading ? (
        <div className="text-center py-10" data-testid="no-results">
          <SearchIcon className="w-10 h-10 text-[#E7E5E4] mx-auto mb-3" />
          <p className="text-sm font-semibold text-[#1C1917]">No translation found for "{query}"</p>
          <p className="text-[10px] text-[#A8A29E] mt-1 mb-5">This word hasn't been added yet</p>

          {submitted ? (
            <div className="bg-[#EAF1EA] rounded-xl p-4 max-w-sm mx-auto">
              <p className="text-sm text-[#588157] font-semibold">Suggestion submitted! A Wirfon will review it.</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 max-w-xs mx-auto">
              {isAuthenticated ? (
                <>
                  {!showSuggest ? (
                    <div className="flex gap-2 w-full">
                      <Button onClick={() => setShowSuggest(true)} size="sm"
                        className="flex-1 bg-[#4CAF50] hover:bg-[#3D8B40] text-white rounded-xl gap-1.5 text-xs" data-testid="suggest-btn">
                        <PenLine className="w-3.5 h-3.5" /> Suggest Translation
                      </Button>
                      <Link to={`/community`} className="flex-1">
                        <Button size="sm" variant="outline" className="w-full rounded-xl gap-1.5 text-xs" data-testid="ask-community-btn">
                          <Users className="w-3.5 h-3.5" /> Ask Community
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="w-full bg-white rounded-2xl border border-[#E7E5E4] p-4 text-left" data-testid="suggest-form">
                      <p className="text-xs font-semibold text-[#1C1917] mb-2">Suggest a translation for "{query}"</p>
                      <input value={suggestForm.translation}
                        onChange={(e) => setSuggestForm(p => ({ ...p, translation: e.target.value }))}
                        placeholder="Your suggested translation..."
                        className="w-full rounded-xl border-0 py-2 px-3 text-xs ring-1 ring-inset ring-[#E7E5E4] focus:ring-[#4CAF50] mb-2"
                        data-testid="suggest-translation-input" />
                      <input value={suggestForm.example}
                        onChange={(e) => setSuggestForm(p => ({ ...p, example: e.target.value }))}
                        placeholder="Example sentence (optional)"
                        className="w-full rounded-xl border-0 py-2 px-3 text-xs ring-1 ring-inset ring-[#E7E5E4] focus:ring-[#4CAF50] mb-2" />
                      <input value={suggestForm.note}
                        onChange={(e) => setSuggestForm(p => ({ ...p, note: e.target.value }))}
                        placeholder="Note or context (optional)"
                        className="w-full rounded-xl border-0 py-2 px-3 text-xs ring-1 ring-inset ring-[#E7E5E4] focus:ring-[#4CAF50] mb-3" />
                      <div className="flex gap-2">
                        <Button onClick={handleSuggest} disabled={submitting || !suggestForm.translation.trim()} size="sm"
                          className="flex-1 bg-[#4CAF50] hover:bg-[#3D8B40] text-white rounded-xl text-xs gap-1" data-testid="submit-suggest-btn">
                          <Send className="w-3 h-3" /> {submitting ? '...' : 'Submit'}
                        </Button>
                        <Button onClick={() => setShowSuggest(false)} size="sm" variant="outline" className="rounded-xl text-xs">
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <Link to="/login">
                  <Button size="sm" className="bg-[#4CAF50] hover:bg-[#3D8B40] text-white rounded-xl text-xs">
                    Sign in to suggest a translation
                  </Button>
                </Link>
              )}
            </div>
          )}
        </div>
      ) : !query ? (
        <div className="text-center py-12">
          <p className="text-xs text-[#A8A29E]">Search Lamnso' or English words to find translations</p>
        </div>
      ) : null}
    </div>
  );
}


function ConversationalText({ text }) {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*|"[^"]*")/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={`ct-${i}`} className="text-[#4CAF50]">{part.slice(2, -2)}</strong>;
        }
        if (part.startsWith('"') && part.endsWith('"')) {
          return <span key={`ct-${i}`}>&ldquo;<em>{part.slice(1, -1)}</em>&rdquo;</span>;
        }
        return <span key={`ct-${i}`}>{part}</span>;
      })}
    </>
  );
}
